<script setup lang="ts">
// MSAL config
import { ut_isLoggedIn, ut_joinPathParts, ut_navigateNewRoute, ut_returnNewRoute } from '@base/utils/utils'
import type * as tp from '@base/utils/types'
import type * as pf from '@amirada/lib-shared'
import { useInitStore } from '@base/stores/initStore'
import { BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser'
import { useAppIndexStore } from '@base/stores/indexStore'

const p = defineProps<{
  isInternStart: boolean
  isHistoric?: boolean
  realCountry: string
}>()

const indexStore = useAppIndexStore()

onMounted(async () => {
  if (import.meta.client) {
    // MSAL
    await msal.initialize()
    msal.handleRedirectPromise().then(handleResponse)
    selectAccount()
  }
})

const msalConfig = {
  auth: {
    clientId: 'dafa4180-919f-4dc3-80bf-46481e6abfca',
    authority: 'https://login.microsoftonline.com/39288a38-ff19-432c-8011-1cd9d0dff445',
    redirectUri: import.meta.client
      ? window.location.origin
      : '',
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
  },
}

// MSAL type of login request
const loginRequest = {
  scopes: ['openid'],
}

// MSAL controller
const msal = new PublicClientApplication(msalConfig)

// MSAL handle redirect response
function handleResponse(response: any) {
  if (response !== null) {
    setAccount(response.account)
  }
  else {
    selectAccount()
  }
}

// MSAL select account
function selectAccount() {
  const currentAccounts = msal.getAllAccounts()
  if (currentAccounts.length === 1) {
    setAccount(currentAccounts[0])
  }
  else if (currentAccounts.length > 1) {
    signOut()
  }
}

// MSAL set accout and login
function setAccount(account: any) {
  const idTokenClaims = account.idTokenClaims
  if (idTokenClaims) {
    let accRcco: string = indexStore.state.paramData.rcco
    let accLcda: string = indexStore.state.paramData.lcda

    // Redirect to account lcc/lcd only if root page and default de/de
    if (p.isInternStart && accRcco === 'de' && accLcda === 'de') {
      const defRcco: string = '_international'
      const defLcda: string = 'en'
      accRcco = idTokenClaims.ctry ? idTokenClaims.ctry.toString() : 'de'
      if (import.meta.client) {
        accLcda = navigator.language ? navigator.language.split('-')[0] || '' : defLcda
      }
      const initData: pf.InitData | undefined = useInitStore().getInitData(indexStore.state.paramData)
      if (initData) {
        const rccoIndex: number = initData.restData.rcco.findIndex(i => i.cod.toLowerCase() === accRcco.toLowerCase())
        const lcdaIndex: number = initData.langData.findIndex(i => i.cod.toLowerCase() === accLcda.toLowerCase())
        accRcco = rccoIndex > -1 ? accRcco.toLowerCase() : defRcco
        accLcda = lcdaIndex > -1 ? accLcda.toLowerCase() : defLcda
      }
    }

    // add groups for testing to special users
    const defUSers: string[] = ['c.froemter@amirada.com', 'thomas.funk@wilo.com', 'christian.guski@wilo.com', 'anja.plessgott@wilo.com', 'nicolas.boiron@wilo.com', 'francoise.gauvin@wilo.com', 'carole.moindrot@wilo.com', 'tobias.gross@wilo.com']
    let groups: string = ''
    const mail: string = idTokenClaims.email?.toString().toLowerCase() || ''
    if (defUSers.includes(mail)) {
      groups = 'Wilo;Vaillant;Vaillant_GROUP_Slovakia;Vaillant_INDUSTRIAL_SLOVAKIA;Vaillant_PROTHERM_Production;Vaillant_Vaillant_GmbH;Industrial_UK_Ltd;SDECCI;SDPR;GlenDimplex;GlenDimplexDE'
    }

    // Login user. Country and language are NOT written here: the url carries them, so writing them
    // into the store alone left the address on the default /de/de while the store said ch/de.
    const paramData: Partial<tp.IParamData> = {
      loggedin: true, // only client side login
      userData: {
        userName: groups.length > 0 ? groups.split(';')[0] : 'WiloStandard',
        firstName: idTokenClaims.given_name?.toString() || '',
        lastName: idTokenClaims.family_name?.toString() || '',
        eMail: idTokenClaims.email?.toString() || '',
        accountId: account.homeAccountId,
      },
    }
    indexStore.setParamDataMutation(paramData)

    // The account's country/language differs from the default: go to the intern start page there.
    // The routing resolves root13 to its slug (web-config) and sets rcco/lcda from the url. Replace,
    // not push, so the back button does not lead to the /de/de page the user never asked for.
    if (accRcco !== indexStore.state.paramData.rcco || accLcda !== indexStore.state.paramData.lcda) {
      ut_navigateNewRoute(ut_joinPathParts(['/', accRcco, accLcda, 'root13']), { rawurl: '1' }, '', false)
    }
  }
}

// MSAL sign in
function signIn() {
  msal.loginRedirect(loginRequest)
}

// MSAL sign out
function signOut() {
  msal.logoutRedirect()
  const paramData: Partial<tp.IParamData> = {
    loggedin: false,
    userData: {},
  }
  indexStore.setParamDataMutation(paramData)
}
</script>

<template>
  <div class="bg-primary py-5">
    <UContainer class="flex justify-between">
      <NuxtLink :to="ut_returnNewRoute(ut_joinPathParts(['/', realCountry, indexStore.state.paramData.lcda, 'root13']), { rawurl: '1' }, '')">
        <UIcon name="i-wilo-logo-white" size="35px" class="mr-2 shrink-0" />
      </NuxtLink>
      <LazySearchInput
        v-if="ut_isLoggedIn()"
        :is-internal="true"
        :use-adesso="false"
        :is-historic="false"
      />
      <div class="flex flex-wrap gap-2 items-center">
        <div v-if="ut_isLoggedIn()" class="hidden md:block text-white font-bold" v-html="indexStore.state.paramData.userData.firstName + ' ' + indexStore.state.paramData.userData.lastName" />
        <!-- text-white: the lock icons carry `currentColor` now, like the other 47 in the set, so
             the colour they used to have baked in is stated here instead. -->
        <UButton :icon="ut_isLoggedIn() ? 'i-wilo-lock-solid-full' : 'i-wilo-lock-open-solid-full'" size="xl" class="text-white" @click="ut_isLoggedIn() ? signOut() : signIn()" />
      </div>
    </UContainer>
  </div>
  <h2 v-if="!ut_isLoggedIn()" class="mt-2 text-center w-full font-bold" v-html="'Internal product and solution finder'" />
  <div class="flex mt-2 w-full">
    <UButton v-if="!ut_isLoggedIn()" class="mx-auto text-white" icon="i-wilo-lock-solid-full" size="xl" label="Login" @click="signIn()" />
  </div>
</template>
